<script lang="ts">
  import { cn } from '$lib/utils';
  import { slide } from 'svelte/transition';
  import Icon, { isIconVariant } from './ui/icon/icon.svelte';
  import { cart } from '$lib/cart';
  import { createDebouncedValue } from '$lib/stores/debounced';
  import { createEventDispatcher, onMount } from 'svelte';
  import { type Deadline, getDailyDeadline, getDeadline, padNumber } from '$lib/utils/date';
  import { FREE_GIFT } from '$lib/constants';
  import { Confetti } from 'svelte-confetti';
  import { getCampaignColors, progressBarConfig } from '$lib/stores/campaigns';
  import { browser } from '$app/environment';

  const dispatch = createEventDispatcher();

  export let showConfettiAnimation: boolean | undefined = undefined;

  $: offset = $progressBarConfig?.tierOffset || -1;
  $: subProgress = offset ? Math.min(+(($debouncedActive / offset) * 100).toFixed(3), 100) : 0;

  let cells: HTMLDivElement[] = Array($progressBarConfig?.tiers.length);

  $: w = cells.slice(1).reduce((acc, cell) => {
    if (acc < cell.clientWidth) return cell.clientWidth;
    return acc;
  }, 0);

  $: tmp = (() => {
    const itemCount =
      $cart?.lines.nodes.reduce((acc, line) => {
        const filter = $progressBarConfig?.tag_filter_type || 'exclude';
        if (
          filter === 'exclude' &&
          $progressBarConfig?.tagFilters.some((exclusion) =>
            line.merchandise.product.tags.includes(exclusion)
          )
        ) {
          return acc;
        }
        if (
          filter === 'include' &&
          $progressBarConfig?.tagFilters.some(
            (exclusion) => !line.merchandise.product.tags.includes(exclusion)
          )
        ) {
          return acc;
        }

        // exclude gifts
        if (
          line.attributes?.some((item) => item.key == FREE_GIFT) ||
          line.cost.totalAmount.amount === '0.0'
        ) {
          return acc;
        }

        return acc + line.quantity;
      }, 0) || 0;

    const currIndex = $progressBarConfig
      ? Math.min(itemCount, $progressBarConfig?.tiers.length + $progressBarConfig?.tierOffset - 1)
      : -1;
    return currIndex;
  })();

  const [active, debouncedActive] = createDebouncedValue(0, 300);
  $: active.set(tmp);

  $: showConfettiTier =
    $progressBarConfig && offset
      ? $debouncedActive < offset
        ? false
        : $progressBarConfig.tiers[$debouncedActive - offset]?.show_confetti
      : false;

  $: showConfetti = showConfettiAnimation && showConfettiTier;

  let countdown: Deadline | undefined = undefined;

  const setDeadline = () => {
    if ($progressBarConfig && $progressBarConfig.countdown_type && $progressBarConfig.ends_in) {
      if ($progressBarConfig.countdown_type === 'datetime') {
        countdown = getDeadline($progressBarConfig.ends_in);
        return;
      }

      if ($progressBarConfig.countdown_type === 'daily') {
        const deadlineDate = new Date($progressBarConfig.ends_in);
        countdown = getDailyDeadline(`${deadlineDate.getHours()}:${deadlineDate.getMinutes()}`);
      }
    }
  };

  $: isPastDeadline =
    !!countdown && countdown.hours === 0 && countdown.minutes === 0 && countdown.seconds === 0;

  $: colors = $progressBarConfig ? getCampaignColors($progressBarConfig?.campaign_color) : null;
  $: altTextColor = colors?.altTextColor;
  $: altBackgroundColor = colors?.altBackgroundColor;
  $: isBlackCampaignColor = colors?.isBlackColor;

  $: {
    if (browser) {
      window.activeCampaignOffset = $debouncedActive;
    }
  }

  onMount(() => {
    setDeadline();
    // set this for automated tests
    window.campaignOffset = $progressBarConfig?.tierOffset || 1;
    // window.campaignDiscount = tieredCampaign.tiers[0].title;
    window.campaignDiscounts =
      $progressBarConfig?.tiers?.map((item) => item.title).join('\n') || '';
  });

  setInterval(() => {
    setDeadline();
  }, 1000);
</script>

{#if $progressBarConfig && $progressBarConfig?.tiers && !!$progressBarConfig.tiers.length && $progressBarConfig.tiers[0]}
  <div
    class="flex flex-row"
    role="progressbar"
    aria-valuenow={subProgress}
    aria-valuemin="0"
    aria-valuemax="100"
  >
    <button
      class="relative w-full block isolate select-none"
      style:--w="{w}px"
      style:--progress="{subProgress}%"
      style:--altBackgroundColor={altBackgroundColor}
      style:--altTextColor={altTextColor}
      on:click={() => dispatch('click')}
    >
      {#if showConfetti}
        <Confetti
          x={[-2, 2]}
          y={[-1, 1]}
          delay={[300, 300]}
          amount={300}
          fallDistance="0px"
          duration={2000}
          colorArray={[isBlackCampaignColor ? '#E8519E' : altTextColor, altBackgroundColor]}
        />
      {/if}

      <div class={cn('flex h-[24px] gap-1')}>
        <div
          class={cn(
            'pl-[5px] pr-2 py-[5px] whitespace-nowrap isolate flex overflow-hidden justify-between items-center gap-1 bg-[var(--altBackgroundColor)] text-[var(--altTextColor)] rounded-xs flex-grow relative',
            'after:absolute after:ease-[cubic-bezier(0.215,_0.610,_0.355,_1.000)] after:-z-10 after:inset-0 after:right-[unset] after:bg-[var(--altTextColor)] after:w-[var(--progress)] after:transition-[width] after:duration-300',
            isBlackCampaignColor && 'after:bg-[var(--altTextColor)]'
          )}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="shrink-0"
          >
            <path
              d="M5.46548 0.853522C5.62332 0.661726 5.70224 0.565828 5.77356 0.496721C6.45707 -0.165574 7.54293 -0.165574 8.22644 0.496721C8.29776 0.565828 8.37668 0.661726 8.53452 0.853523C8.60861 0.943545 8.64565 0.988556 8.68322 1.02881C9.03093 1.40126 9.52282 1.60501 10.0321 1.58751C10.0871 1.58562 10.1451 1.57998 10.2611 1.56871C10.5084 1.5447 10.632 1.5327 10.7313 1.53426C11.6829 1.54926 12.4507 2.31709 12.4657 3.26871C12.4673 3.36801 12.4553 3.49162 12.4313 3.73885C12.42 3.8549 12.4144 3.91292 12.4125 3.96795C12.395 4.47718 12.5987 4.96907 12.9712 5.31678C13.0114 5.35435 13.0565 5.39139 13.1465 5.46548C13.3383 5.62332 13.4342 5.70224 13.5033 5.77356C14.1656 6.45707 14.1656 7.54293 13.5033 8.22644C13.4342 8.29776 13.3383 8.37668 13.1465 8.53452C13.0565 8.60861 13.0114 8.64565 12.9712 8.68322C12.5987 9.03093 12.395 9.52282 12.4125 10.0321C12.4144 10.0871 12.42 10.1451 12.4313 10.2611C12.4553 10.5084 12.4673 10.632 12.4657 10.7313C12.4507 11.6829 11.6829 12.4507 10.7313 12.4657C10.632 12.4673 10.5084 12.4553 10.2611 12.4313C10.1451 12.42 10.0871 12.4144 10.0321 12.4125C9.52282 12.395 9.03093 12.5987 8.68322 12.9712C8.64565 13.0114 8.60861 13.0565 8.53452 13.1465C8.37668 13.3383 8.29776 13.4342 8.22644 13.5033C7.54293 14.1656 6.45707 14.1656 5.77356 13.5033C5.70224 13.4342 5.62332 13.3383 5.46548 13.1465C5.39139 13.0565 5.35435 13.0114 5.31678 12.9712C4.96907 12.5987 4.47718 12.395 3.96795 12.4125C3.91292 12.4144 3.8549 12.42 3.73885 12.4313C3.49162 12.4553 3.36801 12.4673 3.26871 12.4657C2.31709 12.4507 1.54926 11.6829 1.53426 10.7313C1.5327 10.632 1.5447 10.5084 1.56871 10.2611C1.57998 10.1451 1.58562 10.0871 1.58751 10.0321C1.60501 9.52282 1.40126 9.03093 1.02881 8.68322C0.988556 8.64565 0.943545 8.60861 0.853523 8.53452C0.661726 8.37668 0.565828 8.29776 0.496721 8.22644C-0.165574 7.54293 -0.165574 6.45707 0.496721 5.77356C0.565828 5.70224 0.661726 5.62332 0.853522 5.46548C0.943545 5.39139 0.988556 5.35435 1.02881 5.31678C1.40126 4.96907 1.60501 4.47718 1.58751 3.96795C1.58562 3.91292 1.57998 3.8549 1.56871 3.73885C1.5447 3.49162 1.5327 3.36801 1.53426 3.26871C1.54926 2.31709 2.31709 1.54926 3.26871 1.53426C3.36801 1.5327 3.49162 1.5447 3.73885 1.56871C3.8549 1.57998 3.91292 1.58562 3.96795 1.58751C4.47718 1.60501 4.96907 1.40126 5.31678 1.02881C5.35435 0.988556 5.39139 0.943545 5.46548 0.853522Z"
              class={cn(
                'text-[var(--altTextColor)] transition-colors',
                isBlackCampaignColor && 'text-accent',
                subProgress > 0 && 'text-[var(--altBackgroundColor)]',
                subProgress > 0 && isBlackCampaignColor && 'text-accent'
              )}
              fill="currentColor"
            />
            <path
              d="M5.17891 10.1535C5.11902 10.1535 5.09335 10.1107 5.11902 10.0508L8.0108 4.2245C8.02791 4.18173 8.07068 4.16461 8.11346 4.16461H8.81502C8.87491 4.16461 8.90057 4.20739 8.87491 4.26728L5.98313 10.0936C5.96602 10.1364 5.92324 10.1535 5.88046 10.1535H5.17891ZM3.25391 5.89284C3.25391 4.72073 3.75868 4.11328 4.74257 4.11328C5.72646 4.11328 6.23124 4.72073 6.23124 5.89284C6.23124 7.0735 5.71791 7.68095 4.74257 7.68095C3.75868 7.68095 3.25391 7.06495 3.25391 5.89284ZM4.09235 5.89284C4.09235 6.6885 4.28057 7.02217 4.74257 7.02217C5.19602 7.02217 5.3928 6.6885 5.3928 5.89284C5.3928 5.08006 5.19602 4.74639 4.74257 4.74639C4.28057 4.74639 4.09235 5.08006 4.09235 5.89284ZM7.76268 8.41673C7.76268 7.24461 8.26746 6.63717 9.25135 6.63717C10.2352 6.63717 10.74 7.24461 10.74 8.41673C10.74 9.59739 10.2267 10.2048 9.25135 10.2048C8.26746 10.2048 7.76268 9.58884 7.76268 8.41673ZM8.60113 8.41673C8.60113 9.21239 8.78935 9.54606 9.25135 9.54606C9.7048 9.54606 9.90157 9.21239 9.90157 8.41673C9.90157 7.62106 9.7048 7.29595 9.25135 7.29595C8.78935 7.29595 8.60113 7.62106 8.60113 8.41673Z"
              class={cn(
                'text-[var(--altBackgroundColor)] transition-colors',
                isBlackCampaignColor && 'text-highlight',
                subProgress > 0 && 'text-[var(--altTextColor)]',
                subProgress > 0 && isBlackCampaignColor && 'text-highlight'
              )}
              fill="currentColor"
            />
          </svg>
          <p
            class={cn(
              'text-[10px] sm:text-xs transition-colors duration-300',
              subProgress === 100 && 'text-[var(--altBackgroundColor)]',
              subProgress > 0 && isBlackCampaignColor && 'text-[var(--altTextColor)]',
              subProgress === 100 && isBlackCampaignColor && 'text-highlight'
            )}
          >
            {#if $debouncedActive < offset}
              {$progressBarConfig.tiers[0].title}
            {:else}
              {$progressBarConfig.tiers[$debouncedActive - offset].success_title}
            {/if}
          </p>
        </div>

        {#if $debouncedActive < $progressBarConfig.tiers.length + $progressBarConfig.tierOffset - 1 && $progressBarConfig.tiers.length > 1}
          <div
            transition:slide={{ axis: 'x', duration: 300 }}
            class="flex gap-1"
            aria-hidden="true"
          >
            {#each $progressBarConfig.tiers.slice(Math.max(1, 1 + $debouncedActive - offset)) as tier}
              <div
                transition:slide={{ axis: 'x', duration: 300 }}
                class={cn(
                  'gap-1 invisible py-[5px] px-2 overflow-hidden flex justify-between items-center whitespace-nowrap bg-[var(--altBackgroundColor)] text-[var(--altTextColor)] rounded-xs'
                )}
              >
                {#if tier.icon && isIconVariant(tier.icon)}
                  <Icon icon={tier.icon} size="xs" class="shrink-0" />
                {/if}
                <p class="text-[10px] sm:text-xs">{tier.title}</p>
              </div>
            {/each}
          </div>
        {/if}
        {#if countdown && !isPastDeadline}
          <p
            class={cn(
              'flex items-center text-[10px] sm:text-xs/none text-[var(--altTextColor)] tabular-nums pl-2 pr-1.5 whitespace-nowrap'
            )}
          >
            {[
              padNumber(countdown?.hours),
              padNumber(countdown?.minutes),
              padNumber(countdown?.seconds)
            ].join(' : ')}
          </p>
        {/if}
      </div>

      {#if $progressBarConfig.tiers.length > 1}
        <div class="flex absolute -z-10 left-0 top-0 right-0 gap-1 h-full">
          {#each $progressBarConfig.tiers as tier, i}
            <div
              bind:this={cells[i]}
              class={cn(
                'px-2 py-[5px] overflow-hidden flex justify-between items-center whitespace-nowrap gap-1 bg-[var(--altBackgroundColor)] text-[var(--altTextColor)] rounded-xs',
                tier.icon ? 'justify-between' : 'justify-center',
                i === 0 && 'flex-grow invisible pl-[5px] pr-2 py-[5px]'
              )}
            >
              {#if tier.icon && isIconVariant(tier.icon)}
                <Icon icon={tier.icon} size="xs" class="shrink-0" />
              {/if}
              <p class="text-[10px] sm:text-xs">{tier.title}</p>
            </div>
          {/each}

          {#if countdown && !isPastDeadline}
            <p
              class={cn(
                'flex items-center text-[10px] sm:text-xs/none text-[var(--altTextColor)] tabular-nums pl-2 pr-1.5 whitespace-nowrap'
              )}
            >
              {[
                padNumber(countdown?.hours),
                padNumber(countdown?.minutes),
                padNumber(countdown?.seconds)
              ].join(' : ')}
            </p>
          {/if}
        </div>
      {/if}
    </button>

    {#if showConfetti}
      <Confetti
        x={[-2, 2]}
        y={[-1, 1]}
        amount={300}
        delay={[300, 300]}
        fallDistance="0px"
        duration={2000}
        colorArray={[isBlackCampaignColor ? '#E8519E' : altTextColor, altBackgroundColor]}
      />
    {/if}
  </div>
{/if}
